import { gql } from "@apollo/client";
import { Box, FormControlLabel, Switch } from "@mui/material";
import { compact, sortBy } from "lodash";

import AddRemoveList from "~/components/common/AddRemoveList";
import {
  useCreateVisitTypeServiceMutation,
  useDeleteVisitTypeServiceMutation,
  useUpdateVisitTypeServiceMutation,
} from "~/graphql/__generated__/graphql";
import { useOrgConfig, VisitType } from "~/providers/OrgConfigProvider";

const refetchOrgConfig = { awaitRefetchQueries: true, refetchQueries: ["OrgConfig"] };

type Props = {
  visitType: VisitType,
};

const VisitTypeServices = ({ visitType }: Props) => {
  const { services } = useOrgConfig();

  const [createVisitTypeService, { loading: createLoading }] = useCreateVisitTypeServiceMutation();
  const [updateVisitTypeService, { loading: updateLoading }] = useUpdateVisitTypeServiceMutation();
  const [deleteVisitTypeService, { loading: deleteLoading }] = useDeleteVisitTypeServiceMutation();

  const attached = sortBy(visitType.visitTypeServices, vts => vts.service?.name);
  const attachedServiceIds = new Set(compact(attached.map(vts => vts.service?.id)));

  const addOptions = sortBy(
    compact(Object.values(services))
      .filter(service => service.active && !attachedServiceIds.has(service.id))
      .map(service => ({ key: service.id, label: `${service.name} (${service.duration} min)` })),
    "label",
  );

  const items = attached.map(vts => ({
    key: vts.id,
    label: <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", mr: 2 }}>
      <span>{vts.service?.name} ({vts.service?.duration} min)</span>
      <FormControlLabel
        label="Default"
        control={
          <Switch
            size="small"
            checked={vts.selectedByDefault}
            disabled={updateLoading}
            onChange={() => updateVisitTypeService({
              variables: { id: vts.id, selectedByDefault: !vts.selectedByDefault },
              ...refetchOrgConfig,
            })}
          />
        }
      />
    </Box>,
  }));

  return <AddRemoveList
    emptyLabel="No Services Configured"
    items={items}
    addOptions={addOptions}

    addLabel="Add service"
    onAdd={async ([serviceId]) => {
      await createVisitTypeService({
        variables: { visitTypeId: visitType.id, serviceId, selectedByDefault: false },
        ...refetchOrgConfig,
      });
    }}
    addLoading={createLoading}

    onRemove={async (id) => {
      await deleteVisitTypeService({ variables: { id }, ...refetchOrgConfig });
    }}
    removeLoading={deleteLoading}
    removeHelpText="Removing a service does not change visits that have already been scheduled with it."
  />
}

export default VisitTypeServices;

gql`
  mutation CreateVisitTypeService($visitTypeId: Int!, $serviceId: Int!, $selectedByDefault: Boolean)
  @permission(requires: manageOrgConfig)
  {
    createVisitTypeService(visitTypeId: $visitTypeId, serviceId: $serviceId, selectedByDefault: $selectedByDefault) {
      id
    }
  }
`;

gql`
  mutation UpdateVisitTypeService($id: Int!, $selectedByDefault: Boolean!)
  @permission(requires: manageOrgConfig)
  {
    updateVisitTypeService(id: $id, selectedByDefault: $selectedByDefault) {
      id
      selectedByDefault
    }
  }
`;

gql`
  mutation DeleteVisitTypeService($id: Int!)
  @permission(requires: manageOrgConfig)
  {
    deleteVisitTypeService(id: $id) {
      id
      deleted
    }
  }
`;
